import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatSnackBar } from '@angular/material/snack-bar';
import pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import { ToWords } from 'to-words';
import { StoreService } from 'src/app/services/store.service';
import { MaterialModule } from 'src/app/shared/material.module';

pdfMake.vfs = pdfFonts.vfs;
const toWords = new ToWords();

@Component({
  selector: 'app-material-transfer-print',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule, MatProgressBar],
  templateUrl: './material-transfer-print.component.html',
  styleUrl: './material-transfer-print.component.scss'
})
export class MaterialTransferPrintComponent implements OnInit, OnDestroy {
  transferId!: number;
  transferData: any;
  previewData: any = null;
  isDialogMode = false;
  includeSignature = false;
  pdfDoc: any;
  private originalHtmlOverflow = '';
  private originalBodyOverflow = '';

  constructor(
    private route: ActivatedRoute,
    private storeService: StoreService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: { id?: number } | null,
    @Optional() private dialogRef: MatDialogRef<MaterialTransferPrintComponent> | null
  ) {}

  ngOnInit(): void {
    document.body.classList.add('invoice-preview-mode');
    this.originalHtmlOverflow = document.documentElement.style.overflow;
    this.originalBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';

    this.isDialogMode = !!this.dialogData?.id;
    this.transferId = Number(this.dialogData?.id ?? this.route.snapshot.paramMap.get('id'));
    if (this.transferId) {
      this.loadData();
    }
  }

  ngOnDestroy(): void {
    document.body.classList.remove('invoice-preview-mode');
    document.documentElement.style.overflow = this.originalHtmlOverflow;
    document.body.style.overflow = this.originalBodyOverflow;
  }

  printPdf() {
    this.ensurePdfDoc();
    this.pdfDoc?.print();
  }

  downloadPdf() {
    this.ensurePdfDoc();
    this.pdfDoc?.download(`Material-Transfer-${this.previewData?.transferCode || this.transferId}.pdf`);
  }

  async sendMail() {
    this.snackBar.open('Material transfer mail template configured nahi mila. Mail compose open nahi kiya gaya.', 'Close', {
      duration: 4000
    });
  }

  closePreview() {
    this.dialogRef?.close();
  }

  private loadData(): void {
    this.storeService.getMaterialTransferPrintById(this.transferId).subscribe({
      next: (data: any) => {
        this.transferData = Array.isArray(data) ? data[0] : data;
        this.previewData = this.buildPreviewData(this.transferData || {});
        this.ensurePdfDoc();
      },
      error: (err) => console.error('Error loading material transfer print', err)
    });
  }

  private buildPreviewData(source: any) {
    const company = this.pickEntity(source, ['company', 'companymaster', 'companydetails']);
    const fromBranch = this.pickEntity(source, ['frombranch', 'fromBranch', 'sourcebranch']);
    const toBranch = this.pickEntity(source, ['tobranch', 'toBranch', 'destinationbranch']);
    const fromDepartment = this.pickEntity(source, ['fromdepartment', 'fromDepartment', 'warehouse', 'whmaster']);
    const items = this.extractItems(source).map((item: any, index: number) => {
      const quantity = Number(item.quantity || 0);
      const rate = Number(item.rate || 0);
      return {
        sr: index + 1,
        productName: this.safeValue(item.productname || item.itemname || item.description),
        uom: this.safeValue(item.uomname || item.uom),
        batchNo: this.safeValue(item.batchno, ''),
        availableQty: this.formatNumber(item.availablequantity || 0),
        requestedQty: this.formatNumber(item.requestedqty || 0),
        transferQty: this.formatNumber(quantity),
        receivedQty: this.formatNumber(item.receivedquantity || 0),
        rate: this.formatCurrency(rate, false),
        lineAmount: this.formatCurrency(quantity * rate, true)
      };
    });

    const totalQty = items.reduce((sum: number, item: any) => sum + Number(String(item.transferQty).replace(/,/g, '')), 0);
    const totalAmount = Number(source.transferamt || 0);

    return {
      logo: this.resolveImage([
        company.logoBase64,
        company.logobase64,
        company.logo,
        source.logoBase64,
        source.logobase64
      ]),
      signatureImage: this.resolveImage([
        company.signature,
        company.signatureBase64,
        company.signaturebase64,
        source.signature,
        source.signatureBase64
      ]),
      stampImage: this.resolveImage([
        company.stamp,
        company.stampBase64,
        company.stampbase64,
        source.stamp,
        source.stampBase64
      ]),
      companyInitials: this.getInitials(company.name),
      companyName: this.safeValue(company.name, 'VCS ERP'),
      companyAddress: this.joinAddress([company.address1, company.address2, company.city, company.state, company.country, company.pincode]),
      companyMeta: this.buildCompanyMeta(company),
      signatoryName: this.safeValue(
        company.authorized_signatory_name ||
        company.authorizedSignatoryName ||
        source.authorized_signatory_name ||
        source.authorizedSignatoryName,
        ''
      ),
      signatoryTitle: this.safeValue(
        company.authorized_signatory_title ||
        company.authorizedSignatoryTitle ||
        source.authorized_signatory_title ||
        source.authorizedSignatoryTitle,
        'Authorized Signatory'
      ),
      transferCode: this.safeValue(source.code),
      transferDate: this.formatDateValue(source.materialtransferdate),
      challanNo: this.safeValue(source.challanno, ''),
      challanDate: this.formatDateValue(source.challandate),
      status: this.safeValue(source.materialtransferstatusname || source.statusname || source.status, ''),
      transferType: this.safeValue(source.materialtransfertypename || source.materialtransfertype || source.materialtransfertypeid),
      transporterMode: this.safeValue(source.trpotypename || source.trpotype || source.trpotypeid),
      transporterName: this.safeValue(source.trpname, ''),
      transporterDocNo: this.safeValue(source.trpodocno, ''),
      transporterDocDate: this.formatDateValue(source.trpodocdate),
      truckNo: this.safeValue(source.truckno, ''),
      fromBranchName: this.safeValue(fromBranch.branchname || fromBranch.name || source.frombranchname || source.sourcebranchname),
      fromBranchAddress: this.firstMeaningfulText([
        fromBranch.fulladdress,
        this.joinAddress([fromBranch.address1, fromBranch.address2, fromBranch.city, fromBranch.state, fromBranch.country, fromBranch.pincode])
      ]),
      toBranchName: this.safeValue(toBranch.branchname || toBranch.name || source.tobranchname || source.destinationbranchname),
      toBranchAddress: this.firstMeaningfulText([
        toBranch.fulladdress,
        this.joinAddress([toBranch.address1, toBranch.address2, toBranch.city, toBranch.state, toBranch.country, toBranch.pincode])
      ]),
      fromDepartmentName: this.safeValue(fromDepartment.name || fromDepartment.whname || source.fromdepartmentname || source.warehousename),
      items,
      totalItems: items.length,
      totalQty: this.formatNumber(totalQty),
      transferAmount: this.formatCurrency(totalAmount, true),
      amountInWords: `${toWords.convert(totalAmount, { currency: true })}/-`
    };
  }

  private ensurePdfDoc() {
    if (!this.previewData) {
      return;
    }

    const body = [
      [
        { text: 'SR', style: 'tableHeader' },
        { text: 'ITEM', style: 'tableHeader' },
        { text: 'UOM', style: 'tableHeader' },
        { text: 'BATCH', style: 'tableHeader' },
        { text: 'AVAILABLE', style: 'tableHeader' },
        { text: 'REQUESTED', style: 'tableHeader' },
        { text: 'TRANSFER', style: 'tableHeader' },
        { text: 'RECEIVED', style: 'tableHeader' },
        { text: 'RATE', style: 'tableHeader' },
        { text: 'AMOUNT', style: 'tableHeader' }
      ],
      ...this.previewData.items.map((item: any) => ([
        { text: String(item.sr), alignment: 'center' },
        { text: item.productName },
        { text: item.uom, alignment: 'center' },
        { text: item.batchNo || '-', alignment: 'center' },
        { text: item.availableQty, alignment: 'right' },
        { text: item.requestedQty, alignment: 'right' },
        { text: item.transferQty, alignment: 'right', bold: true },
        { text: item.receivedQty, alignment: 'right' },
        { text: item.rate, alignment: 'right' },
        { text: item.lineAmount, alignment: 'right', bold: true }
      ]))
    ];

    const docDefinition: any = {
      pageSize: 'A4',
      pageMargins: [20, 10, 20, 44],
      footer: (currentPage: number, pageCount: number) => ({
        margin: [20, 0, 20, 6],
        stack: [
          {
            canvas: [
              { type: 'line', x1: 0, y1: 0, x2: 555, y2: 0, lineWidth: 1, color: '#8da0b8' },
              { type: 'rect', x: 0, y: 5, w: 132, h: 2.6, color: '#153a6b' },
              { type: 'rect', x: 441, y: 5, w: 114, h: 2.6, color: '#d97706' }
            ]
          },
          {
            margin: [0, 6, 0, 0],
            columns: [
              {
                width: '*',
                text: 'Generated using VCS ERP',
                style: 'footerText'
              },
              {
                width: 90,
                text: `Page ${currentPage} of ${pageCount}`,
                style: 'footerPageText',
                alignment: 'right'
              }
            ]
          }
        ]
      }),
      background: this.previewData.logo
        ? {
            image: this.previewData.logo,
            alignment: 'center',
            width: 300,
            height: 300,
            opacity: 0.048,
            margin: [0, 190, 0, 0]
          }
        : undefined,
      content: [
        {
          canvas: [
            { type: 'rect', x: 0, y: 0, w: 551, h: 6, color: '#153a6b' },
            { type: 'rect', x: 405, y: 0, w: 146, h: 6, color: '#d97706' },
            { type: 'line', x1: 0, y1: 15, x2: 551, y2: 15, lineWidth: 0.7, color: '#e2e8f0' }
          ],
          margin: [0, -10, 0, 4]
        },
        {
          table: {
            widths: [150, '*', 150],
            body: [[
              this.previewData.logo
                ? {
                    stack: [{ image: this.previewData.logo, fit: [76, 76], alignment: 'center', margin: [0, 4, 0, 0] }],
                    border: [false, false, false, false]
                  }
                : {
                    text: this.previewData.companyInitials || 'MT',
                    style: 'logoFallback',
                    border: [false, false, false, false]
                  },
              {
                stack: [
                  { text: this.previewData.companyName, style: 'companyHeader' },
                  this.previewData.companyAddress ? { text: this.previewData.companyAddress, style: 'companyMeta' } : '',
                  ...this.previewData.companyMeta.map((meta: string) => ({ text: meta, style: 'companyMeta' }))
                ].filter(Boolean),
                margin: [0, 3, 0, 0],
                border: [false, false, false, false]
              },
              {
                stack: [
                  { text: 'MATERIAL TRANSFER', style: 'badge' },
                  { text: `Transfer Code: ${this.previewData.transferCode}`, style: 'meta' },
                  { text: `Transfer Date: ${this.previewData.transferDate}`, style: 'meta' },
                  this.previewData.challanNo ? { text: `Challan No: ${this.previewData.challanNo}`, style: 'meta' } : '',
                  this.previewData.challanDate !== '-' ? { text: `Challan Date: ${this.previewData.challanDate}`, style: 'meta' } : ''
                ].filter(Boolean),
                fillColor: '#eef6ff',
                margin: [2, 6, 0, 0],
                border: [true, true, true, true]
              }
            ]]
          },
          layout: {
            hLineColor: () => '#d7deea',
            vLineColor: () => '#d7deea'
          }
        },
        {
          columns: [
            { width: '*', text: `Transfer Type: ${this.previewData.transferType || '-'}`, style: 'summaryBox' },
            { width: '*', text: `Items: ${this.previewData.totalItems}`, style: 'summaryBox' },
            { width: '*', text: `Transfer Qty: ${this.previewData.totalQty}`, style: 'summaryBox' },
            { width: '*', text: `Transfer Amount: ${this.previewData.transferAmount}`, style: 'summaryBox' }
          ],
          columnGap: 8,
          margin: [0, 12, 0, 10]
        },
        {
          columns: [
            {
              width: '*',
              stack: [
                { text: 'From Branch / Issue Point', style: 'sectionHeader' },
                { text: this.previewData.fromBranchName, style: 'detailHeading' },
                this.previewData.fromDepartmentName ? { text: this.previewData.fromDepartmentName, style: 'detailText' } : '',
                this.previewData.fromBranchAddress ? { text: this.previewData.fromBranchAddress, style: 'detailText' } : ''
              ].filter(Boolean),
              fillColor: '#f8fafc',
              margin: [8, 8, 8, 8]
            },
            {
              width: '*',
              stack: [
                { text: 'To Branch / Delivery Point', style: 'sectionHeader' },
                { text: this.previewData.toBranchName, style: 'detailHeading' },
                this.previewData.toBranchAddress ? { text: this.previewData.toBranchAddress, style: 'detailText' } : ''
              ].filter(Boolean),
              fillColor: '#f8fafc',
              margin: [8, 8, 8, 8]
            },
            {
              width: 164,
              stack: [
                { text: 'Transport Details', style: 'sectionHeader' },
                this.previewData.transporterMode ? { text: `Mode: ${this.previewData.transporterMode}`, style: 'detailText' } : '',
                this.previewData.transporterName ? { text: `Transporter: ${this.previewData.transporterName}`, style: 'detailText' } : '',
                this.previewData.transporterDocNo ? { text: `Doc No: ${this.previewData.transporterDocNo}`, style: 'detailText' } : '',
                this.previewData.transporterDocDate !== '-' ? { text: `Doc Date: ${this.previewData.transporterDocDate}`, style: 'detailText' } : '',
                this.previewData.truckNo ? { text: `Vehicle No: ${this.previewData.truckNo}`, style: 'detailText' } : ''
              ].filter(Boolean),
              fillColor: '#f8fafc',
              margin: [8, 8, 8, 8]
            }
          ],
          columnGap: 10,
          margin: [0, 0, 0, 10]
        },
        {
          table: {
            headerRows: 1,
            widths: [20, '*', 30, 42, 42, 42, 42, 40, 40, 54],
            body
          },
          layout: {
            fillColor: (rowIndex: number) => rowIndex === 0 ? '#16323f' : rowIndex % 2 === 0 ? '#f8fafc' : '#ffffff',
            hLineColor: () => '#a7b5c8',
            vLineColor: () => '#a7b5c8'
          }
        },
        {
          columns: [
            {
              width: '*',
              stack: [
                this.previewData.status ? { text: `Transfer Status: ${this.previewData.status}`, style: 'detailText' } : '',
                { text: 'Amount In Words', style: 'sectionHeader', margin: [0, 10, 0, 2] },
                { text: this.previewData.amountInWords, style: 'detailHeading' }
              ].filter(Boolean)
            },
            {
              width: 200,
              stack: [
                { text: `For ${this.previewData.companyName}`, style: 'signatoryCompanyInline', alignment: 'right' },
                this.includeSignature && (this.previewData.stampImage || this.previewData.signatureImage)
                  ? {
                      columns: [
                        {
                          width: 72,
                          stack: [
                            this.previewData.stampImage
                              ? { image: this.previewData.stampImage, fit: [66, 66], opacity: 0.72, alignment: 'right', margin: [0, 4, -8, 0] }
                              : { text: '' }
                          ]
                        },
                        {
                          width: 118,
                          stack: [
                            this.previewData.signatureImage
                              ? { image: this.previewData.signatureImage, fit: [112, 38], alignment: 'right', margin: [0, 8, 0, 0] }
                              : { text: '' }
                          ]
                        }
                      ],
                      columnGap: -12,
                      margin: [0, 4, 0, 0]
                    }
                  : { text: '', margin: [0, 34, 0, 0] },
                this.previewData.signatoryName
                  ? { text: this.previewData.signatoryName, style: 'signatoryName', alignment: 'right', margin: [0, 4, 0, 0] }
                  : { text: '', margin: [0, 10, 0, 0] },
                { text: this.previewData.signatoryTitle || 'Authorized Signatory', style: 'signatoryRole', alignment: 'right' }
              ]
            }
          ],
          columnGap: 16,
          margin: [0, 12, 0, 0]
        }
      ],
      styles: {
        logoFallback: { fontSize: 22, bold: true, color: '#11284c', alignment: 'center', margin: [0, 22, 0, 0] },
        companyHeader: { fontSize: 17.6, bold: true, color: '#11284c', alignment: 'center' },
        companyMeta: { fontSize: 8.7, color: '#334155', alignment: 'center', margin: [0, 1, 0, 0] },
        badge: { fontSize: 11.5, bold: true, color: '#11284c', margin: [10, 10, 10, 7], alignment: 'left' },
        meta: { fontSize: 8.4, color: '#334155', margin: [10, 0, 10, 3], alignment: 'left' },
        summaryBox: { fontSize: 10, bold: true, color: '#0f172a', fillColor: '#f8fafc', margin: [8, 10, 8, 10] },
        sectionHeader: { fontSize: 9.6, bold: true, color: '#0f766e', margin: [0, 0, 0, 4] },
        detailHeading: { fontSize: 10.2, bold: true, color: '#11284c' },
        detailText: { fontSize: 8.9, color: '#334155', margin: [0, 1, 0, 0] },
        tableHeader: { fontSize: 8, bold: true, color: '#ffffff', alignment: 'center' },
        signatoryCompanyInline: { fontSize: 8.6, bold: true, color: '#334155' },
        signatoryName: { fontSize: 9.2, bold: true, color: '#11284c' },
        signatoryRole: { fontSize: 8.7, color: '#475569', bold: true },
        footerText: { fontSize: 6.8, color: '#64748b', margin: [0, 0, 0, 0] },
        footerPageText: { fontSize: 6.8, color: '#64748b' }
      },
      defaultStyle: { fontSize: 9, color: '#0f172a' }
    };

    this.pdfDoc = pdfMake.createPdf(docDefinition);
  }

  private extractItems(source: any): any[] {
    const candidates = [
      source?.materialtransferdetails,
      source?.materialTransferDetails,
      source?.items,
      source?.details
    ];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate;
      }
    }

    return [];
  }

  private pickEntity(source: any, keys: string[]): any {
    for (const key of keys) {
      const value = source?.[key];
      if (value && typeof value === 'object') {
        return Array.isArray(value) ? value[0] || {} : value;
      }
    }
    return {};
  }

  private buildCompanyMeta(company: any): string[] {
    return [
      this.metaLine('GSTIN', company.gstin),
      this.metaLine('PAN', company.pan),
      this.metaLine('Email', company.email),
      this.metaLine('Website', company.website),
      this.metaLine('Phone', company.mobile || company.phone)
    ].filter(Boolean) as string[];
  }

  private resolveImage(candidates: any[]): string | null {
    for (const candidate of candidates) {
      const normalized = this.normalizeImageCandidate(candidate);
      if (normalized) {
        return normalized;
      }
    }
    return null;
  }

  private normalizeImageCandidate(value: any): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const image = value.trim();
    if (!image) {
      return null;
    }

    if (image.startsWith('data:image')) {
      return image;
    }

    if (/^[A-Za-z0-9+/=\r\n]+$/.test(image) && image.length > 120) {
      return `data:image/png;base64,${image.replace(/\s+/g, '')}`;
    }

    return null;
  }

  private metaLine(label: string, value: any): string {
    const normalized = this.normalizePart(value);
    return normalized ? `${label}: ${normalized}` : '';
  }

  private firstMeaningfulText(candidates: any[]): string {
    for (const candidate of candidates) {
      const normalized = this.normalizePart(candidate);
      if (normalized) {
        return normalized;
      }
    }
    return '';
  }

  private joinAddress(parts: any[]): string {
    return parts.map((part) => this.normalizePart(part)).filter(Boolean).join(', ');
  }

  private normalizePart(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    const text = String(value).trim();
    return text === '-' ? '' : text;
  }

  private safeValue(value: any, fallback = '-'): string {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }
    return String(value);
  }

  private formatNumber(value: any): string {
    const amount = Number(value || 0);
    if (Number.isNaN(amount)) {
      return this.safeValue(value);
    }

    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  }

  private formatCurrency(value: any, withSymbol = false): string {
    const amount = Number(value || 0);
    if (Number.isNaN(amount)) {
      return this.safeValue(value);
    }

    const formatted = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);

    return withSymbol ? `${String.fromCharCode(8377)} ${formatted}` : formatted;
  }

  private formatDateValue(value: any): string {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return this.safeValue(value);
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(date);
  }

  private getInitials(value: any): string {
    const text = this.safeValue(value, 'MT');
    return text
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0))
      .join('')
      .toUpperCase();
  }
}
